import { Box } from '@material-ui/core';
import { UnfoldLess, UnfoldMore, PhotoCamera } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { useState, useEffect, useRef } from 'react';
import { Button, PDFPreview, PrintableArea, PrintableAreaRef, Tooltip } from '@roc/ui';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { useLoanStore } from '@roc/feature-loans';
import { useParams } from 'react-router';
import { DocumentForms, useDocumentStore } from '@roc/feature-documents';
import { LoanProcess } from '@roc/feature-utils';
import { VirtualScroll } from './components/common/virtualScroll';
import { StatusFilterField } from './components/common/statusFilterField';
import { PriorityFilterField } from './components/common/priorityFilterField';
import { TaskTodoCardCompponent } from './components/cards/todoTaskCard';
import { useIframeMessaging, filterTodosByStatus } from './utils/todoUtils';
import { mapStatusFilter, SHOW_IN_REVIEW_AND_PENDING_TO_DOS } from './utils/loanDetailsConstants';

export const ServicingInternal = observer(() => {
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const { loanStore } = useLoanStore();
  const { documentStore } = useDocumentStore();
  const params = useParams<{ loanId: string }>();
  const loanId = parseInt(params.loanId);


  const currentForm = documentStore.documentFormStore.currentForm;
  const { prioritiesFilter } = documentStore?.loanTodosStore;
  const [statusFilter, setStatusFilter] = useState(SHOW_IN_REVIEW_AND_PENDING_TO_DOS);
  const [prioritiesSelected, setPrioritiesSelected] = useState([]);
  const [filterBy, setFilterBy] = useState(mapStatusFilter[statusFilter]);
  const printableAreaRef = useRef<PrintableAreaRef>(null);
  const [expandAll, setExpandAll] = useState(0);
  const [collapseAll, setCollapseAll] = useState(0);
  const isTaskFrameworkEnabled = !!loanStore?.loanDetails?.taskFrameworkEnabled;

  const todos =
    documentStore?.loanTodosStore?.todosByLoanProcess?.[LoanProcess.SERVICING] ??
    [];

  const loadTodos = () => {
    documentStore?.loanTodosStore?.fetchTodos(loanId, LoanProcess.SERVICING, null, null);
  };

  useEffect(() => {
    userStore.getUserInfoAndEntitlements();
    loanStore.fetchLoanDetails(loanId);
    loadTodos();
  }, [loanId]);

  useIframeMessaging({
    reloadTodos() {
      loadTodos();
    },
  });

  useEffect(() => {
    if (!currentForm && todos.length > 0) {
      loadTodos();
    }
  }, [currentForm]);

  useEffect(() => {
    loadTodos();
  }, [prioritiesFilter]);

  const download = () => {
    printableAreaRef.current.downloadAsPdf(`Loan ${loanId} Documents.pdf`, {
      singlePage: true,
    });
  };

  if (!globalStore.userFeatures) {
    return null;
  }

  const filteredTodos = filterTodosByStatus(todos, filterBy);

  const onStatusFilterChange = (val) => {
    setStatusFilter(val);
    setFilterBy(mapStatusFilter[val]);
  }

  const onPrioritiesFilterChange = (options) => {
    setPrioritiesSelected(options);
    documentStore?.loanTodosStore.setPrioritiesFilter(options);
  }

  return (
    <VirtualScroll>
      <Box pb={2} display="flex" justifyContent="flex-end">
        <Box pr={2}>
          <Tooltip title="Expand all sections" placement="bottom">
            <div>
              <Button
                testId="expandAll"
                onClick={() => setExpandAll((x) => x + 1)}
                variant="outlined"
                style={{ letterSpacing: 'normal', textTransform: 'none' }}
              >
                <Box height="31px" display="flex" alignItems="center">
                  <UnfoldMore />&nbsp;&nbsp;Expand All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Collapse all sections" placement="bottom">
            <div>
              <Button
                testId="collapseAll"
                onClick={() => setCollapseAll((x) => x + 1)}
                variant="outlined"
                style={{ letterSpacing: 'normal', textTransform: 'none' }}
              >
                <Box height="31px" display="flex" alignItems="center">
                  <UnfoldLess />&nbsp;&nbsp;Collapse All
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        <Box pr={2}>
          <Tooltip title="Take screenshot" placement="bottom">
            <div>
              <Button
                testId="downloadAll"
                onClick={download}
                variant="outlined"
                style={{ letterSpacing: 'normal', textTransform: 'none' }}
              >
                <Box height="31px" display="flex" alignItems="center">
                  <PhotoCamera />&nbsp;&nbsp;Take Screenshot
                </Box>
              </Button>
            </div>
          </Tooltip>
        </Box>
        {isTaskFrameworkEnabled && (
          <Box pr={2}>
            <PriorityFilterField
              value={prioritiesSelected}
              onChange={onPrioritiesFilterChange}
            />
          </Box>
        )}
        <StatusFilterField
          value={statusFilter}
          onChange={onStatusFilterChange}
          version="v2"
        />
      </Box>
      <PrintableArea ref={printableAreaRef}>
        <TaskTodoCardCompponent
          loanProcess={LoanProcess.SERVICING}
          todos={filteredTodos}
          showSectionActions
          showDateColumn={true}
          showStatusColumn={true}
          showPriorityColumn={true}
          showAssigneeColumn={true}
          showFileName
          expandAll={expandAll}
          collapseAll={collapseAll}
          showDownloadTodo
          filteredBy={filterBy}
        />
      </PrintableArea>
      <DocumentForms loanId={loanId?.toString()} />
      <PDFPreview
        open={documentStore.documentPreview != null}
        title={documentStore.documentPreview?.title}
        data={documentStore.documentPreview?.data}
        dataV2={documentStore.documentPreview?.data}
        headers={documentStore.documentPreview?.headers}
        onClose={() => documentStore.closeDocumentPreview()}
      />
    </VirtualScroll>
  );
});